import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@app/core/services/auth.service';
import { getUserDevice } from '@app/core/utils/helpers';
import { CreateStoreService } from '@app/features/store-invitation/create-store.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { I18nService } from '@app/core/services/i18n.service';
import { TAXI_OFFICE_KEYS } from '@app/core/i18n/keys/taxi-office.keys';
import { ProviderPortalKind } from '@app/core/models/portal-session.model';
import { ToastService } from '@shared/services/toast.service';

@Component({
  selector: 'app-provider-login-setup',
  templateUrl: './provider-login-setup.component.html',
  styleUrls: ['./provider-login-setup.component.scss']
})
export class ProviderLoginSetupComponent implements OnInit {
  readonly keys = TAXI_OFFICE_KEYS;
  form: FormGroup;
  submitted = false;
  credentialId: string | null = null;
  /** The secret from the e-mailed link (?token=...): the API refuses the invitation without it. */
  invitationToken: string | null = null;
  errorMessage = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly createStoreService: CreateStoreService,
    private readonly authService: AuthService,
    private readonly spinner: NgxSpinnerService,
    private readonly router: Router,
    private readonly i18n: I18nService,
    private readonly toast: ToastService
  ) {
    this.form = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.credentialId = this.route.snapshot.paramMap.get('id');
    this.invitationToken = this.route.snapshot.queryParamMap.get('token');
  }

  get passwordMismatch(): boolean {
    const password = this.form.get('password')?.value;
    const confirm = this.form.get('confirmPassword')?.value;
    return !!password && !!confirm && password !== confirm;
  }

  submit(): void {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.credentialId || this.form.invalid || this.passwordMismatch) {
      return;
    }

    const body = {
      password: this.form.value.password,
      invitationToken: this.invitationToken,
      ...getUserDevice()
    };

    this.spinner.show();
    this.createStoreService.acceptProviderInvitation(this.credentialId, body).subscribe(
      (raw: unknown) => {
        this.spinner.hide();
        const res = (raw || {}) as AcceptProviderInvitationResponse;
        if (res.authToken) {
          // A bus operator opens the bus portal, a taxi office the taxi one: opening the taxi
          // pages for a bus operator is what showed an error after a successful setup.
          const portal = portalKindOf(res);
          this.authService.applyProviderLoginResponse({ authToken: res.authToken, provider: res.provider }, portal);
          void this.i18n.syncWithActivePortal();
          this.toast.success(this.i18n.instant(this.keys.setup.success));
          void this.router.navigate([portal === 'TRANSIT_OPERATOR' ? '/transit/dashboard' : '/taxi/overview']);
        } else {
          // The password is set; only the automatic sign-in did not happen.
          this.toast.success(this.i18n.instant(this.keys.setup.successSignIn));
          void this.router.navigate(['/auth/login']);
        }
      },
      (err: any) => {
        this.spinner.hide();
        // The API says why (an expired link, for one); otherwise the generic message.
        const message = err && err.error && (err.error.error || err.error.message);
        this.errorMessage = message || this.i18n.instant(this.keys.setup.errorInvitation);
      }
    );
  }
}

interface AcceptProviderInvitationResponse {
  authToken?: string;
  provider?: Record<string, unknown> & { providerType?: string };
  providerPortalKind?: ProviderPortalKind;
}

/** The portal the API names; older APIs did not send it, then the provider type decides. */
function portalKindOf(res: AcceptProviderInvitationResponse): ProviderPortalKind {
  if (res.providerPortalKind === 'TRANSIT_OPERATOR' || res.providerPortalKind === 'TAXI_OFFICE') {
    return res.providerPortalKind;
  }
  return res.provider?.providerType === 'BUS_OPERATOR' ? 'TRANSIT_OPERATOR' : 'TAXI_OFFICE';
}
